import { Component, Input, OnInit } from '@angular/core';
import { translate } from '@shared-libs/helpers';
import Quill, { Delta } from 'quill';
import ImageResize from 'quill-image-resize-module';
import ImageCompress from 'quill-image-compress';
import { FormControl } from '@angular/forms';
import { CommunicationChannel } from '@internal-models/communication-template.model';
import { QuillModules } from 'ngx-quill';
Quill.register('modules/imageResize', ImageResize);
Quill.register('modules/imageCompress', ImageCompress);
const DeltaObject = Quill.import('delta') as typeof Delta;

@Component({
	selector: 'editor-component',
	templateUrl: 'editor.component.html',
	styleUrls: ['./editor.component.scss'],
})
export class EditorComponent implements OnInit {
	@Input() public template: string;
	@Input() public variables: Array<string>;
	@Input() public type: CommunicationChannel.Email | CommunicationChannel.Sms | 'contract' | 'text';

	public templateVariables: Array<{ variable: string; translation: string }>;
	public editorModules: QuillModules = {};
	public form: FormControl;
	private editor!: Quill;

	constructor() {}

	public ngOnInit(): void {
		this.templateVariables = this.convertVariables(this.variables as Array<string>);
		this.form = new FormControl(this.formatVariablesToHtml(this.template));
		this.initializeQuillEditorModules();
		this.initializeQuillWrapperElement();
	}

	public onEditorCreated(editor: Quill): void {
		this.editor = editor;
	}

	public getFormattedHtml(): string {
		const parsedHTML = this.editor.root.cloneNode(true);
		return this.formatHtmlToVariables(parsedHTML as HTMLDivElement);
	}

	/**
	 * Insert a variable in the editor text at the location of the cursor
	 * @param variable The variable object to add to the text
	 * @param variable.variable The parsable variable
	 * @param variable.translation The human readable variable
	 */
	public insertVariable(variable: { variable: string; translation: string }): void {
		const selection = this.editor.getSelection(true);
		this.editor.insertEmbed(selection.index, 'variable', variable);
		setTimeout(() => this.editor.setSelection(selection.index + 1, 0));
		this.form.patchValue(this.editor.root.innerHTML);
	}

	/**
	 * Format the variables of the stored template to human readable
	 * We use the regex to get all character between {{...}} then mapping
	 * that parsable variable with human readable variable using the converted variables
	 * We check for the {{...}} between >< to exclude the parsable variable as attribute
	 * @param html The template as html string
	 * @returns {string} The html with human readable variables
	 */
	private formatVariablesToHtml(html: string): string {
		return (html ?? '').replace(
			/>{{(.*?)}}</g,
			(match: string, group: string) =>
				`>${
					this.templateVariables.find((variable) => variable.variable === `{{${group}}}`)?.translation ??
					`{{${group}}}`
				}<`
		);
	}

	/**
	 * Replace the used variable from human readable to the parsable variables i.e. {{...}}
	 * @param html The html as HTML element
	 * @returns {string} The parsed html as string with variables
	 */
	private formatHtmlToVariables(html: HTMLDivElement): string {
		const variables = html.getElementsByTagName('variable');
		for (const element of Array.from(variables)) {
			const variable = element.getAttribute('variable');
			element.children.item(0).textContent = variable;
		}
		return html.innerHTML;
	}

	/**
	 * Convert the variable from Array of variable keys to an object with added translations
	 * to show human readable variables
	 * @param variables The parsable variables i.e. {{...}}
	 * @returns The variables as array of objects with the parsable & human readable variables
	 */
	private convertVariables(variables: Array<string> = []): Array<{ variable: string; translation: string }> {
		return variables.map((variable) => ({
			variable,
			translation: translate(`TEMPLATE_VARIABLES.${variable.replace(/[{}]/g, '')}`),
		}));
	}

	/**
	 * Quill has to add a wrapper element around input, by default it is a P-tag. For sms we don't want any
	 * extra spacing, we want everything inline, so we use SPAN-tags.
	 */
	private initializeQuillWrapperElement(): void {
		const Block = Quill.import('blots/block');
		Block.tagName = this.type === CommunicationChannel.Sms ? 'SPAN' : 'P';
		Quill.register(Block, true);
	}

	/**
	 * Initialize the Quill editor config based on the type of template
	 */
	private initializeQuillEditorModules(): void {
		this.editorModules = {
			imageResize: { modules: ['Resize', 'DisplaySize'] },
			counter: { container: '#counter', max: 160 },
			imageCompress: {
				quality: 0.4,
				maxWidth: 1000,
				maxHeight: 1000,
				imageType: 'image/jpeg',
			},
			...(this.type === 'sms'
				? {
						keyboard: {
							bindings: {
								enter: {
									key: 13,
									shiftKey: null,
									handler: () => {},
								},
								tab: {
									key: 9,
									shiftKey: null,
									handler: () => {},
								},
							},
						},
					}
				: {}),
			clipboard: {
				matchVisual: this.type === 'sms' ? false : true,
				matchers: this.type === 'sms' ? [['BR', () => new DeltaObject().insert(' ')]] : [],
			},
			toolbar:
				this.type !== 'sms'
					? [
							['bold', 'italic', 'underline', 'strike'],
							['blockquote'],
							[{ list: 'ordered' }, { list: 'bullet' }],
							[{ script: 'sub' }, { script: 'super' }],
							[{ indent: '-1' }, { indent: '+1' }],
							[{ size: ['10px', '12px', '14px', '16px', '18px', '20px', '22px', '24px', '26px'] }],
							[{ header: [1, 2, 3, 4, 5, 6, false] }],
							[{ color: [] }, { background: [] }],
							[{ align: [] }],
							['image'],
							this.type === 'text' ? ['link'] : [],
						]
					: [],
		};
	}
}

/**
 * Create an embedded blot to use to add a variable with styling as element to the editor
 */
const Embed = Quill.import('blots/embed');
class VariableBlot extends Embed {
	static create(value: { variable: string; translation: string }) {
		const node: HTMLElement = super.create();
		node.setAttribute('class', '_variable');
		node.innerHTML = value?.translation ?? '';
		node.setAttribute('contenteditable', 'false');
		node.setAttribute('variable', value.variable);
		return node;
	}

	static value(node: HTMLElement) {
		return { variable: node.getAttribute('variable'), translation: node.innerText };
	}
}
VariableBlot.blotName = 'variable';
VariableBlot.tagName = 'variable';
Quill.register(VariableBlot);

/**
 * Create a custom link sanitizer to sanitize URL's against protocols
 * and add https:// when not added to not break links
 */
const Link = Quill.import('formats/link');
// Override the existing property on the Quill global object and add custom protocols
Link.PROTOCOL_WHITELIST = ['http', 'https', 'mailto', 'tel'];

class CustomLinkSanitizer extends Link {
	static sanitize(url: string) {
		// Run default sanitize method from Quill
		const sanitizedUrl = super.sanitize(url);

		// Not whitelisted URL based on protocol so, let's return `blank`
		if (!sanitizedUrl || sanitizedUrl === 'about:blank') return sanitizedUrl;

		// Verify if the URL already have a whitelisted protocol
		const hasWhitelistedProtocol = this.PROTOCOL_WHITELIST.some((protocol) => {
			return sanitizedUrl.startsWith(protocol);
		});

		if (hasWhitelistedProtocol) return sanitizedUrl;

		// if not, then append only 'https' to not to be a relative URL
		return `https://${sanitizedUrl}`;
	}
}

Quill.register(CustomLinkSanitizer, true);

class CharacterCounter {
	quill: Quill;
	options: { container: string; max?: number };

	constructor(quill: Quill, options: { container: string; max?: number }) {
		this.quill = quill;
		this.options = options;

		const container = document.querySelector(this.options.container);

		if (container) {
			this.quill.on('editor-change', () => {
				const text = this.quill.getText().trim();
				let color = 'inherit';

				if (this.options.max) {
					// Character limitation
					// if (quill.getLength() > this.options.max) {
					// 	quill.deleteText(this.options.max, quill.getLength());
					// }
					color =
						text.length >= (this.options.max / 100) * 90 && text.length < this.options.max
							? '#edb142'
							: text.length >= this.options.max
								? '#EB4B24'
								: 'inherit';
				}

				container.innerHTML = `<span style="color:${color};">${text.length}${
					this.options.max ? `/${this.options.max}` : ''
				} ${translate('CHARACTERS')}</span>`;
			});
		}
	}
}

/**
 * Create and register a word/character counter for sms
 */
Quill.register('modules/counter', CharacterCounter);
